import { makeAutoObservable } from "mobx";
import { Subject } from "rxjs";
import { ItemDropDownOptions, SystemItemTypes } from "../enums";
import SubscriptionStore from "./SubscriptionStore";
import PaymentStore from "./PaymentStore";
import InvoiceStore from "./InvoiceStore";
import ContactStore from "./ContactStore";
import OrderStore from "./OrderStore";
import {
  IAPIPaginationResponse,
  PaginationModel,
  ToastService,
} from "@mindmemobile/mm-shared-global";
import { ItemGridModel } from "../models/ItemGridModel";
import { DonationGridModel } from "../models/DonationGridModel";
import {
  ProductItemModel,
  ProductVariantEditableAdvancedModel,
  ProductVariantEditableModel,
} from "../models/ProductVariantEditableModel";
import { ItemMembershipModel } from "../models/ItemMembershipModel";
import { ItemServiceModel } from "../models/ItemServiceModel";
import { ItemDonationModel } from "../models/ItemDonationModel";
import { CheckoutGridModel } from "../models/CheckoutGridModel";
import { DonarsGridModel } from "../models/DonarsGridModel";
import { GeneralSettingsModel } from "../models/GeneralSettingsModel";
import { SelectedItemModel } from "../models/SelectedItemModel";
import {
  IAPIGetEditProductModel,
  IAPIProductReferenceInPurchaseResponse,
} from "../interfaces/IAPIProductModels";
import {
  IAPIDonarsGridModel,
  IAPIDonationGridModel,
  IAPIGetEditDonationModel,
} from "../interfaces/IAPIDonationModels";
import { IAPIItemsGridModel } from "../interfaces/IAPIItemsGridModel";
import { IAPICheckoutGridModel } from "../interfaces/IAPICheckoutModel";
import { IAPIGetMembershipForEditResponse } from "../interfaces/IAPIMembershipModels";
import { IAPIGetServiceForEditResponseModel } from "../interfaces/IAPIServiceModels";
import AuthService from "../services/AuthService";
import ProductService from "../services/ProductService";
import { ProductAttributeMappingsInput } from "../models/ProductModel";
import FundingStore from "./FundingStore";

export default class ItemsStore {
  public itemsGridData: ItemGridModel[] = [];
  public donationsGridData: DonationGridModel[] = [];
  public searchValue: string = "";
  public checkoutSearchValue: string = "";
  public checkoutGridPagination: PaginationModel = new PaginationModel({
    pageSize: 10,
  });
  public itemGridPagination: PaginationModel = new PaginationModel({
    pageSize: 10,
  });
  public donationGridPagination: PaginationModel = new PaginationModel({
    pageSize: 10,
  });
  public searchSubject = new Subject<boolean>();
  public checkoutSearchSubject = new Subject<boolean>();
  public selectedProduct: ProductItemModel | null = null;
  public selectedMembership: ItemMembershipModel | null = null;
  public selectedService: ItemServiceModel | null = null;
  public selectedDonation: ItemDonationModel | null = null;
  public selectedItemDetails: {
    id: string;
    type: keyof typeof SystemItemTypes;
  } | null = null;
  public showItemsFilter: boolean = false;
  public showProductDeletePopUp: boolean = false;
  public subscriptionStore: SubscriptionStore = new SubscriptionStore();
  public paymentStore: PaymentStore = new PaymentStore();
  public invoiceStore: InvoiceStore = new InvoiceStore();
  public contactStore: ContactStore = new ContactStore();
  public fundingStore: FundingStore = new FundingStore();
  public orderStore: OrderStore = new OrderStore();
  public checkoutGridData: CheckoutGridModel[] = [];
  public purchasedItemDetails: { id: string; lastPurchase: string } | null =
    null;

  public donarsGridData: DonarsGridModel[] = [];
  public donarGridPagination: PaginationModel = new PaginationModel({
    pageSize: 10,
  });
  public isDonorGirdActive: boolean = false;
  public breadCrumbData?: SelectedItemModel | null = null;
  public isTestMode: boolean = false;
  public generalSettings: GeneralSettingsModel = new GeneralSettingsModel();

  constructor() {
    makeAutoObservable(this);
  }

  toggleTestMode(): void {
    this.isTestMode = !this.isTestMode;
  }

  updateGeneralSettings(data: GeneralSettingsModel): void {
    this.generalSettings = new GeneralSettingsModel(data);
  }

  setBreadCrumbData(breadCrumbData?: SelectedItemModel): void {
    this.breadCrumbData = breadCrumbData;
  }

  toggleDonorGridActive(): void {
    this.isDonorGirdActive = !this.isDonorGirdActive;
  }

  setPurchasedItemDetails(
    data: IAPIProductReferenceInPurchaseResponse | null
  ): void {
    if (!data) return;
    this.purchasedItemDetails = data;
  }

  setSelectedItemDetails(type: keyof typeof SystemItemTypes, id: string): void {
    this.selectedItemDetails = { id: id, type: type };
  }

  deserializeDonarsGridData(
    data: IAPIDonarsGridModel[],
    pagination: IAPIPaginationResponse
  ): void {
    const mappedData = data.map((item) => DonarsGridModel.deserialize(item));
    this.donarGridPagination = PaginationModel.deserialize(pagination);
    this.donarsGridData = mappedData;
  }

  toggleItemsFilter(): void {
    this.showItemsFilter = !this.showItemsFilter;
  }

  toggleShowProductDeletePopup(): void {
    this.showProductDeletePopUp = !this.showProductDeletePopUp;
  }

  updateSearchValue(value: string, route?: ItemDropDownOptions): void {
    let shouldTriggerSearch = false;

    switch (route) {
      case ItemDropDownOptions.Items:
        if (this.searchValue !== value) {
          this.searchValue = value;
          this.itemGridPagination = new PaginationModel({
            pageNumber: 1,
            pageSize: 10,
          });
          shouldTriggerSearch = true;
        }
        break;
      case ItemDropDownOptions.Checkout:
        if (this.checkoutSearchValue !== value) {
          this.checkoutSearchValue = value;
          this.checkoutGridPagination = new PaginationModel({
            pageNumber: 1,
            pageSize: 10,
          });
          this.checkoutSearchSubject.next(true);
        }
        break;
      case ItemDropDownOptions.Subscriptions:
        if (this.subscriptionStore.searchValue !== value) {
          this.subscriptionStore.updateSearchValue(value);
        }
        break;
      case ItemDropDownOptions.Payments:
        if (this.paymentStore.searchValue !== value) {
          this.paymentStore.updateSearchValue(value);
          this.paymentStore.searchSubject.next(true);
        }
        break;
      case ItemDropDownOptions.Invoices:
        if (this.invoiceStore.searchValue !== value) {
          this.invoiceStore.updateSearchValue(value);
          this.invoiceStore.searchSubject.next(true);
        }
        break;
      case ItemDropDownOptions.Contacts:
        if (this.contactStore.searchValue !== value) {
          this.contactStore.updateSearchValue(value);
        }
        break;
      case ItemDropDownOptions.Donations:
        if (this.searchValue !== value) {
          this.searchValue = value;
          this.donationGridPagination = new PaginationModel({
            pageNumber: 1,
            pageSize: 10,
          });
          shouldTriggerSearch = true;
        }
        break;
      default:
        break;
    }

    if (shouldTriggerSearch) {
      this.searchSubject.next(true);
    }
  }

  deserializeGridData(
    data: IAPIItemsGridModel[],
    pagination: IAPIPaginationResponse,
    excludePaginationChange: boolean = false
  ): void {
    const mappedData = data.map((item) => ItemGridModel.deserialize(item));
    if (!excludePaginationChange) {
      this.itemGridPagination = PaginationModel.deserialize(pagination);
    }
    this.itemsGridData = mappedData;
  }

  deserializeWithoutPagination = (data: IAPIItemsGridModel[]): void => {
    const mappedData = data.map((item) => ItemGridModel.deserialize(item));
    this.itemsGridData = mappedData;
  };

  deserializeDonationGridData(
    data: IAPIDonationGridModel[],
    pagination: IAPIPaginationResponse
  ): void {
    const mappedData = data.map((item) => DonationGridModel.deserialize(item));
    this.donationGridPagination = PaginationModel.deserialize(pagination);
    this.donationsGridData = mappedData;
  }

  deserializeCheckoutGridData(
    data: IAPICheckoutGridModel[],
    pagination: IAPIPaginationResponse,
    excludePaginationChange: boolean = false
  ): void {
    const mappedData = data.map((item) => CheckoutGridModel.deserialize(item));
    if (!excludePaginationChange) {
      this.checkoutGridPagination = PaginationModel.deserialize(pagination);
    }
    this.checkoutGridData = mappedData;
  }

  updatePagination(data: PaginationModel): void {
    this.itemGridPagination = new PaginationModel(data);
  }

  updateDonationGridPagination(data: PaginationModel): void {
    this.donationGridPagination = new PaginationModel(data);
  }

  constructProductItemModel(
    data: IAPIGetEditProductModel,
    showBreadCrumb = true
  ): void {
    this.selectedProduct = ProductItemModel.deserializeAPIModel(data);
    this.breadCrumbData = showBreadCrumb
      ? new SelectedItemModel({
          title: this.selectedProduct?.detailSection?.name || "",
          route: "/app/items",
        })
      : null;
  }

  constructMembershipItemModel(
    data: IAPIGetMembershipForEditResponse,
    showBreadCrumb = true
  ): void {
    this.selectedMembership = ItemMembershipModel.deserializeAPIModel(data);
    this.breadCrumbData = showBreadCrumb
      ? new SelectedItemModel({
          title: this.selectedMembership?.detailSection?.name || "",
          route: "/app/items",
        })
      : null;
  }

  updateSelectedMembership(data: ItemMembershipModel): void {
    this.selectedMembership = new ItemMembershipModel(data);
  }

  constructDonationItemModel(
    data: IAPIGetEditDonationModel,
    showBreadCrumb = true
  ): void {
    this.selectedDonation = ItemDonationModel.deserializeAPIModel(data);
    this.breadCrumbData = showBreadCrumb
      ? new SelectedItemModel({
          title: this.selectedDonation?.detailSection?.name || "",
          route: "/app/items",
        })
      : null;
  }

  updateSelectedDonation(data: ItemDonationModel): void {
    this.selectedDonation = new ItemDonationModel(data);
  }

  constructServiceItemModel(
    data: IAPIGetServiceForEditResponseModel,
    showBreadCrumb = true
  ): void {
    this.selectedService = ItemServiceModel.deserializeAPIModel(data);
    this.breadCrumbData = showBreadCrumb
      ? new SelectedItemModel({
          title: this.selectedService?.detailSection?.name || "",
          route: "/app/items",
        })
      : null;
  }

  updateSelectedService(data: ItemServiceModel): void {
    this.selectedService = new ItemServiceModel(data);
  }

  toggleEditVariants(isEditVariant: boolean): void {
    const newProductItem = new ProductItemModel({
      ...this.selectedProduct,
      isEditVariant: isEditVariant,
      selectedVariant: isEditVariant
        ? this.selectedProduct?.variants?.options[
            this.selectedProduct.selectedVariantIndex
          ]
        : undefined,
    });

    if (isEditVariant)
      newProductItem.editableVariant =
        ProductVariantEditableModel.init(newProductItem);
    else newProductItem.editableVariant = undefined;
    this.updateSelectedProduct(newProductItem);
  }

  updateSelectedProduct(data: ProductItemModel): void {
    this.selectedProduct = new ProductItemModel(data);
  }

  validateEditableVariant(): boolean {
    const isValid = this.selectedProduct?.editableVariant?.validate() || false;
    this.updateSelectedProduct(
      new ProductItemModel({ ...this.selectedProduct })
    );

    return isValid;
  }

  async onSaveVariant(): Promise<boolean> {
    if (!this.selectedProduct?.editableVariant) {
      return false;
    }

    const isValid = this.validateEditableVariant();
    if (!isValid) {
      return false;
    }

    const request = this.selectedProduct?.editableVariant?.createAPIModel();
    if (!request) {
      return false;
    }

    const accountId = AuthService.authData.accountId;
    request.productId = this.selectedProduct?.detailSection.id || "";
    request.accountId = accountId;

    try {
      const result = await ProductService.updateVariant(request).toPromise();

      if (result) {
        ToastService.showSuccess("Variant updated successfully");

        if (request.productId === this.selectedProduct?.detailSection.id) {
          const clonedProduct = new ProductItemModel({
            ...this.selectedProduct,
          });
          if (clonedProduct.editableVariant?.advanced) {
            clonedProduct.advancedSection =
              new ProductVariantEditableAdvancedModel({
                ...clonedProduct.editableVariant.advanced,
              });
          }

          if (clonedProduct.variants) {
            const updatedVariant =
              clonedProduct.editableVariant?.attribute?.attributeMappings;
            clonedProduct.variants.options.forEach((option) => {
              if (option.attributeMappings) {
                option.attributeMappings.forEach((mapping) => {
                  updatedVariant?.forEach((element) => {
                    if (
                      mapping.attributeId === element.attributeId &&
                      mapping.optionId === element.optionId
                    ) {
                      mapping.optionName = element.optionName;
                      mapping.color = element.color;
                      mapping.valueType = element.valueType;
                      mapping.type = element.type;
                    }
                  });
                });
              }
            });
            clonedProduct.selectedVariant = new ProductAttributeMappingsInput(
              clonedProduct.editableVariant?.attribute
            );
          }

          this.updateSelectedProduct(clonedProduct);
        }
        this.toggleEditVariants(false);

        return true;
      }
    } catch (error) {
      ToastService.showError("Error in updating variant");
      return false;
    }

    return false;
  }
}
